<!-- TODO: isolate program > composables/smaller files before moving to composition API -->
<!-- Instead of signedInEvents.sort(), we should sort by date -->

<template>
  <!-- one heading (Seán, 2026-11-03): the event this page is for, e.g. VIZBI Online Masterclass;
       VIZBI Online alone until it's known -->
  <!-- the heading, and on its line at the right the time zone menu, as the program page's
       (Program/TimeZone.vue's compact menu; Seán, 2026-11-03); signed in only (2026-10-22):
       signed out, there are no times to show -->
  <div class="titleRow">
    <h3>VIZBI <i>Online</i><span v-if="firstEvent" class="eventType">{{ firstEvent }}</span></h3>
    <TimeZoneInline v-if="isSignedIn" variant="compact" class="timeZone" />
  </div>
  <div v-if="!showEvents()" id="signin">
    This page will provide access the Masterclass and VIZBI videos after they have streamed.
  </div>
  <div v-else-if="!isSignedIn" class="clearBoth">
    <p>Please <a href="#sign-in" target="_self">sign in</a> to access the live streams.</p>
  </div>
  <div v-else-if="!signedInEvents.length" class="clearBoth">
    <p>Your account doesn't include access to the live streams for {{ year }}.</p>
  </div>
  <div v-else class="clearBoth">
    <!-- what the page is for, then the one thing to check, a paragraph each (Seán, 2026-11-03) -->
    <p class="intro">This page gives you access to the live sessions of the VIZBI <i>Online</i>
      events.</p>
    <p class="intro">Before you join, please check you have the
      <a href="https://zoom.us/download">latest Zoom desktop client</a>.</p>
    <!-- <p class="intro">
      If you need help, you can contact the VIZBI support team via
      <a href="https://app.purechat.com/w/byqeil">this chat link</a>
    </p> -->
    <!-- one event at a time (Seán, 2026-11-03): the one on show, then those coming up after it,
         each collapsed into a row -->
    <LiveEvent
      v-for="eventName in orderedEvents"
      :key="eventName"
      :event-name="eventName"
      :time-zone="timeZone"
      :is-upcoming="eventName !== currentEvent"
      @range="setRange" />
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`Live: ${msg}`); // eslint-disable-line
import {getVideoAccessNow} from "@/composables/videoAccessNow";
import {computed, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {DateTime} from "luxon";
import TimeZoneInline from "@/components/TimeZoneInline.vue";
import LiveEvent from "@/components/LiveEvent";
import history from "@/assets/history.json";
import showEvents from "@/composables/useShowEvents";
import useUserSafe from "@/composables/useUserSafe";
import useEventRegistration from "@/composables/useEventRegistration";
import useRoles from "@/composables/useRoles";
const year = history.latestYear;
const timeZone = ref("");
const {isSignedIn, user} = useUserSafe();
const {isRegisteredOnline} = useEventRegistration(year);
// an event's live session: registered for it online, or its year's video access (a purchase);
// the alumni offer's registrants have the role but not the year (videoAccessNow.js)
function getCanJoin(eventType, videoAccess) {
  if (isRegisteredOnline(eventType)) return true;
  return (videoAccess[`${eventType}VideosUntil`] || 0) >= year;
}
// admins and testers see /live as a registrant does, both events, to check and test it
// (Seán, 2026-11-03: the agent's tester account looks for itself)
const {isAdmin, isTester} = useRoles();
const signedInEvents = computed(() => {
  if (!isSignedIn.value) return [];
  if (isAdmin.value || isTester.value) return [`Masterclass ${year}`, `VIZBI ${year}`];
  const videoAccess = getVideoAccessNow(user.value?.publicMetadata?.videoAccess);
  const result = [];
  if (getCanJoin("workshop", videoAccess)) result.push(`VIZBI ${year}`);
  if (getCanJoin("masterclass", videoAccess)) result.push(`Masterclass ${year}`);
  return result.sort();
});
// "Masterclass 2026" → "Masterclass", "VIZBI 2026" → "Workshop"
function getEventType(eventName) {
  return eventName.startsWith("Masterclass") ? "Masterclass" : "Workshop";
}
// each event's span ({firstStart, lastEnd}, LiveEvent.vue), null once long over
const ranges = ref({});
function setRange({eventName, span}) {
  ranges.value = {...ranges.value, [eventName]: span};
}
// the event on show: the first still to come or on, or over within the last day (its end
// state); one with no span yet (its program loading) waits its turn in the list's order
const isCurrent = (span) => !span || span.lastEnd.plus({days: 1}) > DateTime.now();
const orderedEvents = computed(() => {
  const getStart = (eventName) => ranges.value[eventName]?.firstStart?.toMillis() ?? Infinity;
  const isKnown = (eventName) => ranges.value[eventName] !== null;
  const known = signedInEvents.value.filter(isKnown);
  return [...known].sort((first, second) => getStart(first) - getStart(second));
});
const currentEvent = computed(() => {
  return orderedEvents.value.find((eventName) => isCurrent(ranges.value[eventName])) || "";
});
const firstEvent = computed(() => (currentEvent.value ? getEventType(currentEvent.value) : ""));
// a reload is a new visit, in the state everyone sees (Seán, 2026-11-03): the test mode is gone
// (it lives in memory), and so is a dialog's address left from it; Join the Meeting opens again
// by itself if it's due (useLiveMeeting.js)
const [route, router] = [useRoute(), useRouter()];
if (route.hash === "#join-meeting") router.replace({path: route.path, query: route.query});
onMounted(() => log(`mounted`));
</script>

<style scoped>
/* the heading and the time zone menu on one line, the menu at the right on the heading's
   baseline (as the program page's); on a narrow screen the menu wraps below */
.titleRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  /* ▶ EDIT HERE: the row gap shows only when the menu wraps under the heading (a phone):
     22px from the heading (Seán, 2026-11-03); on one line, 16px between them */
  gap: 22px 16px;
  margin-bottom: 8px; /* ▶ EDIT HERE: space between the heading and the intro */
}
.titleRow h3 {
  margin: 0;
  color: var(--text-color-strong); /* the page's brightest line */
}
.timeZone {
  font-size: 14px;
}
/* /live's vertical rhythm (Seán, 2026-11-03; DESIGN.md § 6): one gap between every block (the
   intro, the buttons, Earlier today, the end state, the cards, Coming up), a
   smaller one between the cards, the smallest between paragraphs; LiveEvent.vue and
   LiveEndState.vue read them */
.clearBoth {
  clear: both; /* needed to clear two floats above */
  margin-top: 5px;
  --live-section-gap: 24px; /* ▶ EDIT HERE: between the page's blocks */
  --live-card-gap: 16px; /* ▶ EDIT HERE: between the session cards */
  --live-paragraph-gap: 8px; /* ▶ EDIT HERE: between paragraphs */
}
/* the introduction: a step below the heading, smaller and muted (as /feedback's) */
p.intro {
  margin: 0 0 var(--live-paragraph-gap);
  font-size: 1rem;
  color: color-mix(in srgb, var(--text-color) 70%, transparent);
}
p.intro:last-of-type {
  margin-bottom: 20px; /* ▶ EDIT HERE: space between the introduction and the buttons */
}
div.description {
  text-align: left;
  text-decoration: none;
  font-size: 1.1rem;
  color: black;
}
/* a space set explicitly: Vue drops the whitespace between two tags (DESIGN.md § B.2) */
h3 .eventType {
  margin-left: 0.25em;
}
/* a phone (Seán, 2026-11-03): the introduction's paragraphs and the cards a little further
   apart than on the desktop; the menu to the text below as before */
@media (max-width: 600px) {
  .titleRow {
    margin-bottom: 16px;
  }
  .clearBoth {
    --live-paragraph-gap: 16px;
    --live-card-gap: 20px;
  }
}
</style>
